import React from "react";

/**
 * Custom Nivo layers for the upstream-to-downstream box plots.
 *
 * Each factory closes over the data it needs and returns a layer component.
 * Nivo hands a layer the computed `boxPlots` (position, size, the five
 * quantile pixel positions and the sample count), the band `xScale` keyed by
 * site name, the `yScale` and the plot's inner size.
 */

const LANDMARK_STYLE = {
  prf: { fill: "rgba(46, 125, 50, 0.10)", stroke: "#2e7d32" },
  reservoir: { fill: "rgba(2, 119, 189, 0.10)", stroke: "#0277bd" },
  confluence: { fill: "rgba(109, 76, 65, 0.10)", stroke: "#6d4c41" },
  outfall: { fill: "rgba(198, 40, 40, 0.08)", stroke: "#c62828" },
  other: { fill: "rgba(0, 0, 0, 0.06)", stroke: "#546e7a" },
};

/**
 * Gutter rows for landmark labels: each label claims a pixel span around its
 * center and takes the first row where it overlaps nothing already placed, so
 * two landmarks over the same stretch (the reservoir and a confluence into
 * it) never print on top of each other.
 * @param {array} placed [{center, label}] in pixels
 * @param {number} maxRows rows available in the gutter
 * @returns {number[]} a row per item, in the same order
 */
export const packLabelRows = (placed, maxRows = 3) => {
  const rows = [];
  const taken = [];
  placed.forEach(({ center, label }) => {
    const half = (String(label).length * 6.2) / 2 + 8;
    const span = [center - half, center + half];
    let row = taken.findIndex(
      (spans) => !spans.some(([a, b]) => span[0] < b && span[1] > a)
    );
    if (row < 0) {
      row = Math.min(taken.length, maxRows - 1);
      if (taken.length < maxRows) taken.push([]);
    }
    taken[row].push(span);
    rows.push(row);
  });
  return rows;
};

/** How many gutter rows a set of landmarks may need, for the chart margin. */
export const landmarkRows = (landmarks) =>
  Math.min(3, Math.max(1, (landmarks ?? []).length));

/**
 * Landmarks between sites. A stretch (a PRF, the reservoir) is a light band
 * between the two site centers; a confluence is a dotted line at the midpoint
 * between the sites that bracket it, since it is a point on the stream, not a
 * stretch of it. Labels sit in the gutter above the plot on a bracket or tick,
 * stacked into rows when they would collide; the layer draws inside the
 * margin-translated group, so a negative y lands in that gutter.
 * @param {array} landmarks [{landmarkId, label, kind, afterLocationName, beforeLocationName}]
 */
export const makeLandmarksLayer = (landmarks) => {
  const LandmarksLayer = ({ xScale, innerHeight }) => {
    if (!landmarks?.length || typeof xScale?.bandwidth !== "function")
      return null;
    const half = xScale.bandwidth() / 2;

    const placed = landmarks
      .map((lm) => {
        const xa = xScale(lm.afterLocationName);
        const xb = xScale(lm.beforeLocationName);
        if (xa == null || xb == null) return null;
        const x1 = Math.min(xa, xb) + half;
        const x2 = Math.max(xa, xb) + half;
        const isPoint = lm.kind === "confluence";
        return {
          lm,
          x1,
          x2,
          isPoint,
          center: isPoint ? (x1 + x2) / 2 : (x1 + x2) / 2,
          label: lm.label,
          style: LANDMARK_STYLE[lm.kind] ?? LANDMARK_STYLE.other,
        };
      })
      .filter(Boolean);
    const rows = packLabelRows(placed);

    return (
      <g aria-hidden="true">
        {placed.map((it, i) => {
          const yTop = -4 - rows[i] * 14;
          return (
            <g key={it.lm.landmarkId}>
              {it.isPoint ? (
                <>
                  <line
                    x1={it.center}
                    x2={it.center}
                    y1={0}
                    y2={innerHeight}
                    stroke={it.style.stroke}
                    strokeOpacity={0.7}
                    strokeDasharray="3 3"
                  />
                  <line
                    x1={it.center}
                    x2={it.center}
                    y1={yTop}
                    y2={0}
                    stroke={it.style.stroke}
                    strokeWidth={1}
                  />
                </>
              ) : (
                <>
                  <rect
                    x={it.x1}
                    y={0}
                    width={it.x2 - it.x1}
                    height={innerHeight}
                    fill={it.style.fill}
                  />
                  {[it.x1, it.x2].map((x) => (
                    <line
                      key={x}
                      x1={x}
                      x2={x}
                      y1={0}
                      y2={innerHeight}
                      stroke={it.style.stroke}
                      strokeOpacity={0.5}
                      strokeDasharray="2 4"
                    />
                  ))}
                  <line
                    x1={it.x1}
                    x2={it.x2}
                    y1={yTop}
                    y2={yTop}
                    stroke={it.style.stroke}
                    strokeWidth={1}
                  />
                  {[it.x1, it.x2].map((x) => (
                    <line
                      key={`tick-${x}`}
                      x1={x}
                      x2={x}
                      y1={yTop}
                      y2={rows[i] === 0 ? 0 : yTop + 4}
                      stroke={it.style.stroke}
                      strokeWidth={1}
                    />
                  ))}
                </>
              )}
              <text
                x={it.center}
                y={yTop - 4}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill={it.style.stroke}
              >
                {it.label}
              </text>
            </g>
          );
        })}
      </g>
    );
  };
  return LandmarksLayer;
};

/**
 * The sample count above each box, "ND" when the box is mostly non-detects,
 * and the raw samples drawn over any box built from fewer than `limit`
 * values. A non-detect sample is drawn hollow.
 * @param {Map} boxesByKey "site|period" to the enriched box from the hook
 * @param {number} limit boxes with fewer values than this show their samples
 */
export const makeSamplesLayer = (boxesByKey, limit) => {
  const SamplesLayer = ({ boxPlots, yScale, innerHeight }) => (
    <g>
      {boxPlots.map((bp) => {
        const box = boxesByKey?.get(`${bp.group}|${bp.subGroup}`);
        const n = bp.data?.n ?? box?.recordcount ?? 0;
        const cx = bp.x + bp.width / 2;
        const pixels = bp.coordinates?.values ?? [];
        const top = pixels.length ? Math.min(...pixels) : bp.y;
        const jitterStep = Math.min(3, bp.width / 8);
        return (
          <g key={bp.key}>
            <text
              x={cx}
              y={top - 5}
              textAnchor="middle"
              fontSize={10}
              fill="#546e7a"
            >
              {`n=${n}`}
              {box?.mostlyNondetect ? " ND" : ""}
            </text>
            {box && n < limit
              ? box.values.map((v, i) => {
                  const cy = yScale(v.resultValue);
                  if (!Number.isFinite(cy) || cy < 0 || cy > innerHeight)
                    return null;
                  return (
                    <circle
                      key={`${v.collectDate}-${i}`}
                      cx={cx + ((i % 5) - 2) * jitterStep}
                      cy={cy}
                      r={2.5}
                      fill={v.nondetect ? "#fff" : bp.color ?? "#37474f"}
                      stroke="#37474f"
                      strokeWidth={1}
                    >
                      <title>
                        {`${v.collectDate}: ${v.resultValue}${
                          v.nondetect ? " (non-detect)" : ""
                        }`}
                      </title>
                    </circle>
                  );
                })
              : null}
          </g>
        );
      })}
    </g>
  );
  return SamplesLayer;
};
